/**
 * TenantScopePicker — V226 + V227 / CONN-SCOPE.
 *
 * Multi-select picker for cross-tenant resource sharing with optional
 * per-instance refinement (V227). Used by:
 *   • Build > Connectors create flow (generic connectors)
 *   • External Agent Frameworks create form (platform connectors)
 *   • The "Assign to tenants" row action slideout
 *
 * Emits a list of "grants" — each grant is `{tenantId, instanceIds}`:
 *   - `instanceIds: []` or omitted → cascade: covers every instance under tenant
 *   - `instanceIds: ["uuid", ...]` → restricted to those specific instances
 *
 * UI shape:
 *   - Top of each tenant row: checkbox + tenant name + (optional) per-instance count chip
 *   - When the tenant is selected, an "All instances ▾" toggle exposes a
 *     per-instance checkbox list lazily loaded via api.listInstancesForTenant.
 *     "All instances" (default) corresponds to cascade; switching to
 *     "Specific instances…" surfaces the per-instance checkboxes.
 *
 * Backward note: legacy callers that pass a `string[]` for `value` are
 * coerced to the granular shape on read (each id becomes a cascade grant);
 * `onChange` always emits the granular shape. Migrate callers in one pass
 * — see ConnectorAssignTenantsSlideout for the canonical wiring.
 *
 * @param {Array<{tenantId, instanceIds?}>|string[]} value  current grants
 * @param {(grants:Array<{tenantId, instanceIds:string[]}>)=>void} onChange
 * @param {string}   ownerTenantId — pinned, excluded from grants
 * @param {string}   orgId         — caller's org (scopes the tenant fetch)
 * @param {boolean}  readOnly
 * @param {string}   ariaLabel
 * @param {string}   emptyHint     — bottom-line copy when no grants selected
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Search, X, Check, Users, AlertCircle, Loader2, Lock,
  ChevronDown, ChevronRight, Layers, Server,
} from 'lucide-react';
import * as api from '../../services/api';

const ROW_HEIGHT = 32;

/**
 * Coerce legacy `string[]` value into the granular grant shape so the
 * picker can render either input cleanly. Each legacy tenantId becomes
 * a cascade grant. Modern shape is passed through.
 */
function normalizeGrants(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      if (typeof entry === 'string') return { tenantId: entry, instanceIds: [] };
      if (entry && typeof entry === 'object' && entry.tenantId) {
        const ids = Array.isArray(entry.instanceIds) ? entry.instanceIds.filter(Boolean) : [];
        return { tenantId: entry.tenantId, instanceIds: ids };
      }
      return null;
    })
    .filter(Boolean);
}

export default function TenantScopePicker({
  value = [],
  onChange,
  ownerTenantId = null,
  orgId = null,
  readOnly = false,
  ariaLabel = 'Tenant scope',
  emptyHint = 'No additional tenants — resource will be scoped to your tenant only.',
}) {
  const [tenants, setTenants] = useState(null); /* null = loading; [] = loaded-empty */
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const requestIdRef = useRef(0);

  /* V227 — per-tenant instance state. Two layers:
   *   instanceCache[tenantId] = { loading, error, instances } — lazy fetched.
   *   expandedTenantId = the tenant row currently showing its instance list.
   *
   * We hydrate per-tenant cache on demand (click "Specific instances…" or
   * expand the chevron). Avoids fan-out fetching for every tenant on mount. */
  const [instanceCache, setInstanceCache] = useState({});
  const [expandedTenantIds, setExpandedTenantIds] = useState(() => new Set());

  // Fetch tenant list on mount / org change.
  useEffect(() => {
    const reqId = ++requestIdRef.current;
    setTenants(null);
    setError(null);
    /* Try the org-scoped endpoint first when we have an orgId — Org Admin
     * gets a precise within-org list. Fall back to the global getTenants()
     * when no orgId is on the session (Platform Admin pre-customer-switch). */
    const fetch = orgId && typeof api.getTenantsByOrg === 'function'
      ? api.getTenantsByOrg(orgId)
      : (typeof api.getTenants === 'function' ? api.getTenants() : Promise.resolve(null));
    fetch
      .then((res) => {
        if (reqId !== requestIdRef.current) return;
        if (res && res.ok === false) {
          setError(res.error || res.message || `Failed to load tenants (HTTP ${res.status || '?'})`);
          setTenants([]);
          return;
        }
        /* Tolerate three response shapes the BE has used historically:
         *   1. Flat array — `getTenantsByOrg` returns ResponseEntity<List<Tenant>>
         *      and apiFetch wraps as {ok, data: [...]}. → res.data is the array.
         *   2. {items: [...]} or {tenants: [...]} or {content: [...]} —
         *      paginated/wrapped variants.
         *   3. Raw array on `res` (no envelope).
         * Anything else falls through to []. */
        const body = (res && 'data' in res) ? res.data : res;
        let list = null;
        if (Array.isArray(body)) list = body;
        else if (body && Array.isArray(body.items)) list = body.items;
        else if (body && Array.isArray(body.tenants)) list = body.tenants;
        else if (body && Array.isArray(body.content)) list = body.content;
        setTenants(Array.isArray(list) ? list : []);
      })
      .catch((e) => {
        if (reqId !== requestIdRef.current) return;
        setError(e?.message || String(e));
        setTenants([]);
      });
  }, [orgId]);

  // Normalize incoming value into granular shape once per change.
  const grants = useMemo(() => normalizeGrants(value), [value]);
  // Map tenantId -> grant for O(1) lookup. Re-derived per grants change.
  const grantsByTenantId = useMemo(() => {
    const m = new Map();
    grants.forEach((g) => m.set(g.tenantId, g));
    return m;
  }, [grants]);

  // Owner tenant resolved against the loaded list (for chip name).
  const ownerTenant = useMemo(() => {
    if (!ownerTenantId || !Array.isArray(tenants)) return null;
    return tenants.find((t) => t.id === ownerTenantId) || null;
  }, [tenants, ownerTenantId]);

  // Selectable list: every tenant EXCEPT the owner.
  const selectableTenants = useMemo(() => {
    if (!Array.isArray(tenants)) return [];
    const out = ownerTenantId ? tenants.filter((t) => t.id !== ownerTenantId) : tenants.slice();
    out.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    if (!search.trim()) return out;
    const q = search.trim().toLowerCase();
    return out.filter((t) => {
      const hay = [t.name, t.code, t.id].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [tenants, ownerTenantId, search]);

  const emitChange = useCallback((nextGrants) => {
    if (readOnly || typeof onChange !== 'function') return;
    // Always emit the granular shape. Cascade grants carry instanceIds = [].
    onChange(nextGrants.map((g) => ({
      tenantId: g.tenantId,
      instanceIds: Array.isArray(g.instanceIds) ? g.instanceIds : [],
    })));
  }, [readOnly, onChange]);

  const toggleTenant = (tenantId) => {
    if (readOnly) return;
    const has = grantsByTenantId.has(tenantId);
    let next;
    if (has) {
      next = grants.filter((g) => g.tenantId !== tenantId);
      setExpandedTenantIds((prev) => {
        const n = new Set(prev); n.delete(tenantId); return n;
      });
    } else {
      next = [...grants, { tenantId, instanceIds: [] }]; // default cascade
    }
    emitChange(next);
  };

  const setInstanceIdsFor = (tenantId, instanceIds) => {
    if (readOnly) return;
    const next = grants.map((g) =>
      g.tenantId === tenantId
        ? { ...g, instanceIds: Array.isArray(instanceIds) ? instanceIds : [] }
        : g
    );
    emitChange(next);
  };

  const toggleInstance = (tenantId, instanceId) => {
    const g = grantsByTenantId.get(tenantId);
    if (!g) return;
    const cur = new Set(g.instanceIds || []);
    if (cur.has(instanceId)) cur.delete(instanceId); else cur.add(instanceId);
    setInstanceIdsFor(tenantId, Array.from(cur));
  };

  // Lazy-fetch instances for a tenant the first time its row expands.
  const ensureInstancesLoaded = useCallback(async (tenantId) => {
    if (!tenantId) return;
    setInstanceCache((prev) => {
      if (prev[tenantId]) return prev;
      return { ...prev, [tenantId]: { loading: true, error: null, instances: null } };
    });
    try {
      const res = await api.listInstancesForTenant?.(tenantId);
      if (res && res.ok === false) {
        setInstanceCache((prev) => ({
          ...prev,
          [tenantId]: { loading: false, error: res.error || 'Failed to load instances', instances: [] },
        }));
        return;
      }
      const list = (res && 'data' in res) ? res.data : res;
      const sorted = Array.isArray(list)
        ? list.slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        : [];
      setInstanceCache((prev) => ({
        ...prev,
        [tenantId]: { loading: false, error: null, instances: sorted },
      }));
    } catch (e) {
      setInstanceCache((prev) => ({
        ...prev,
        [tenantId]: { loading: false, error: e?.message || String(e), instances: [] },
      }));
    }
  }, []);

  const toggleExpanded = async (tenantId) => {
    setExpandedTenantIds((prev) => {
      const n = new Set(prev);
      if (n.has(tenantId)) n.delete(tenantId); else n.add(tenantId);
      return n;
    });
    if (!instanceCache[tenantId]) {
      await ensureInstancesLoaded(tenantId);
    }
  };

  const selectAll = () => {
    if (readOnly) return;
    emitChange(selectableTenants.map((t) => ({ tenantId: t.id, instanceIds: [] })));
  };
  const clearAll = () => emitChange([]);

  // Count for header summary.
  const selectedCount = grants.length;
  const specificCount = grants.filter((g) => g.instanceIds && g.instanceIds.length > 0).length;

  return (
    <div className="flex flex-col gap-2" aria-label={ariaLabel}>
      {/* Header — owner pin + count summary + bulk actions */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold">
            <Users size={11} aria-hidden="true" /> Tenant scope
          </span>
          {ownerTenant && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-v2-sm bg-v2-muted text-v2-ink text-[10.5px] font-medium border border-v2-border-subtle"
              title="Owner tenant — automatically included, cannot be removed"
            >
              <Lock size={9} aria-hidden="true" />
              {ownerTenant.name || 'Owner'}
              <span className="text-v2-ink-muted">· owner</span>
            </span>
          )}
          {!ownerTenant && ownerTenantId && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-v2-sm bg-v2-muted text-v2-ink-muted text-[10.5px]">
              <Lock size={9} aria-hidden="true" /> owner (loading…)
            </span>
          )}
          <span className="text-[10.5px] text-v2-ink-muted">
            {selectedCount} additional tenant{selectedCount === 1 ? '' : 's'}
            {specificCount > 0 && (
              <span className="ml-1 text-v2-info-strong">
                · {specificCount} per-instance
              </span>
            )}
          </span>
        </div>
        {!readOnly && selectableTenants.length > 0 && (
          <div className="flex items-center gap-2 text-[10.5px]">
            <button type="button" onClick={selectAll} className="text-v2-action hover:underline">
              Select all
            </button>
            <span className="text-v2-ink-muted">·</span>
            <button type="button" onClick={clearAll} className="text-v2-action hover:underline">
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-v2-ink-muted" aria-hidden="true" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter tenants…"
          disabled={readOnly || tenants == null}
          className="w-full bg-v2-surface border border-v2-border rounded-v2-md pl-7 pr-2 py-1.5 text-[12.5px] text-v2-ink v2-focus-ring"
        />
      </div>

      {/* List */}
      <div
        className="border border-v2-border rounded-v2-md bg-v2-surface max-h-96 overflow-y-auto"
        role="listbox"
        aria-multiselectable="true"
      >
        {tenants == null && (
          <div className="flex items-center justify-center gap-2 py-4 text-[12px] text-v2-ink-muted">
            <Loader2 size={13} className="animate-spin" aria-hidden="true" />
            <span>Loading tenants…</span>
          </div>
        )}
        {tenants != null && error && (
          <div className="flex items-center gap-2 px-3 py-3 text-[12px] text-v2-danger-strong bg-v2-danger-soft">
            <AlertCircle size={13} aria-hidden="true" /> {error}
          </div>
        )}
        {tenants != null && !error && selectableTenants.length === 0 && (
          <div className="flex items-start gap-2 px-3 py-3 text-[12px] text-v2-ink-muted">
            {tenants.length === 0 ? (
              /* Three legitimate sub-cases lead here. Distinguish them so the
                * operator knows whether to file a bug, switch role, or accept
                * the empty list as the correct state for a single-tenant org. */
              <span>
                <strong className="text-v2-ink">No other tenants in this org.</strong>{' '}
                You can still create this resource — it will be scoped to your
                tenant only. To share with additional tenants, an Org Admin
                must first create more tenants in this organization.
                {!orgId && (
                  <span className="block mt-1 text-v2-warning-strong">
                    Note: your session has no orgId — falling back to the global
                    tenants list. Switch into an organization to scope properly.
                  </span>
                )}
              </span>
            ) : search.trim() !== '' ? (
              <span>No tenants match “{search}”.</span>
            ) : (
              <span>
                <strong className="text-v2-ink">No additional tenants to share with.</strong>{' '}
                The only tenant in this org is the owner tenant ({ownerTenant?.name || 'yours'})
                — sharing requires at least one sibling tenant.
              </span>
            )}
          </div>
        )}
        {tenants != null && !error && selectableTenants.map((t) => {
          const grant = grantsByTenantId.get(t.id);
          const checked = !!grant;
          const expanded = expandedTenantIds.has(t.id);
          const isCascade = !grant || !grant.instanceIds || grant.instanceIds.length === 0;
          const instCount = grant && Array.isArray(grant.instanceIds) ? grant.instanceIds.length : 0;
          const cache = instanceCache[t.id];

          return (
            <div key={t.id} className="border-b border-v2-border-subtle last:border-b-0">
              {/* Top row — tenant checkbox + scope summary chip */}
              <div className="flex items-center">
                <button
                  type="button"
                  role="option"
                  aria-selected={checked}
                  onClick={() => toggleTenant(t.id)}
                  disabled={readOnly}
                  className={`flex-1 flex items-center gap-2 px-3 text-left transition-colors ${
                    checked ? 'bg-v2-action-soft text-v2-action-strong' : 'hover:bg-v2-muted text-v2-ink'
                  } ${readOnly ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
                  style={{ height: ROW_HEIGHT }}
                >
                  <span className={`inline-flex items-center justify-center w-4 h-4 rounded-v2-sm border ${
                    checked ? 'bg-v2-action border-v2-action text-white' : 'bg-v2-surface border-v2-border'
                  }`}>
                    {checked && <Check size={10} aria-hidden="true" />}
                  </span>
                  <span className="text-[12.5px] font-medium flex-1 truncate">{t.name || '(unnamed)'}</span>
                  {checked && (
                    <span
                      className={`text-[10px] px-1.5 py-[1px] rounded-v2-sm whitespace-nowrap ${
                        isCascade
                          ? 'bg-v2-muted text-v2-ink-muted'
                          : 'bg-v2-info-soft text-v2-info-strong font-semibold'
                      }`}
                      title={isCascade
                        ? 'All instances under this tenant'
                        : `${instCount} specific instance${instCount === 1 ? '' : 's'}`}
                    >
                      {isCascade ? 'all instances' : `${instCount} inst`}
                    </span>
                  )}
                  {t.code && <span className="text-[10.5px] text-v2-ink-muted font-mono-v2">{t.code}</span>}
                </button>
                {checked && !readOnly && (
                  <button
                    type="button"
                    onClick={() => toggleExpanded(t.id)}
                    className="px-2 py-1 text-v2-ink-muted hover:text-v2-ink hover:bg-v2-muted"
                    title={expanded ? 'Hide instance picker' : 'Restrict to specific instances'}
                    aria-expanded={expanded}
                    style={{ height: ROW_HEIGHT }}
                  >
                    {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  </button>
                )}
              </div>

              {/* Expanded — per-instance picker */}
              {checked && expanded && (
                <div className="pl-9 pr-3 py-2 bg-v2-page border-t border-v2-border-subtle">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-1.5 text-[10.5px] text-v2-ink-muted">
                      <Layers size={10} aria-hidden="true" /> Instance restriction
                    </div>
                    <div className="flex items-center gap-2 text-[10.5px]">
                      <button
                        type="button"
                        onClick={() => setInstanceIdsFor(t.id, [])}
                        className={isCascade ? 'text-v2-action-strong font-semibold' : 'text-v2-action hover:underline'}
                      >
                        All instances
                      </button>
                      <span className="text-v2-ink-muted">·</span>
                      <button
                        type="button"
                        onClick={() => {
                          // Pre-select all loaded instances so "Specific" surfaces with everything ticked;
                          // operator then unticks what they don't want. Better than empty + greyed save.
                          const all = (cache?.instances || []).map((i) => i.id);
                          if (all.length > 0) setInstanceIdsFor(t.id, all);
                        }}
                        disabled={!cache?.instances || cache.instances.length === 0}
                        className={!isCascade ? 'text-v2-action-strong font-semibold' : 'text-v2-action hover:underline disabled:text-v2-ink-muted disabled:no-underline'}
                      >
                        Specific…
                      </button>
                    </div>
                  </div>

                  {cache?.loading && (
                    <div className="flex items-center gap-2 text-[11px] text-v2-ink-muted py-2">
                      <Loader2 size={11} className="animate-spin" aria-hidden="true" />
                      <span>Loading instances…</span>
                    </div>
                  )}
                  {cache?.error && (
                    <div className="text-[11px] text-v2-danger-strong py-2">{cache.error}</div>
                  )}
                  {cache && !cache.loading && !cache.error && cache.instances?.length === 0 && (
                    <div className="text-[11px] text-v2-ink-muted py-2 italic">
                      This tenant has no instances configured yet.
                    </div>
                  )}
                  {cache && !cache.loading && !cache.error && cache.instances?.length > 0 && (
                    <div className="flex flex-col gap-1">
                      {cache.instances.map((inst) => {
                        const selected = !isCascade && (grant.instanceIds || []).includes(inst.id);
                        return (
                          <button
                            key={inst.id}
                            type="button"
                            onClick={() => toggleInstance(t.id, inst.id)}
                            disabled={readOnly}
                            className={`flex items-center gap-2 px-2 py-1 rounded-v2-sm text-[11.5px] text-left ${
                              selected
                                ? 'bg-v2-action-soft text-v2-action-strong'
                                : 'hover:bg-v2-muted text-v2-ink'
                            } ${isCascade ? 'opacity-60' : ''}`}
                            title={isCascade ? 'Pick "Specific…" above to restrict to certain instances' : undefined}
                          >
                            <span className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded-v2-sm border ${
                              selected ? 'bg-v2-action border-v2-action text-white' : 'bg-v2-surface border-v2-border'
                            }`}>
                              {selected && <Check size={9} aria-hidden="true" />}
                            </span>
                            <Server size={10} className="text-v2-ink-muted" aria-hidden="true" />
                            <span className="flex-1 truncate">{inst.name || '(unnamed)'}</span>
                            {inst.tier && (
                              <span className="text-[9.5px] text-v2-ink-muted uppercase tracking-wider font-semibold">
                                {inst.tier}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer hint */}
      <div className="text-[10.5px] text-v2-ink-muted leading-snug">
        {selectedCount === 0
          ? emptyHint
          : <>Shared tenants get <strong>read + use</strong> access. Only your tenant can edit credentials or revoke. <strong>All instances</strong> grants cascade to every environment under that tenant; <strong>Specific</strong> grants limit to the ticked instances only.</>
        }
      </div>
    </div>
  );
}
